import { useTranslation } from "react-i18next";

interface Section {
  h?: string;
  p: string;
}

interface Props {
  /** i18n namespace under `seoLongform` — e.g. "home", "expertise", "gymTraining", "footballCategory" */
  ns: "home" | "expertise" | "gymTraining" | "footballCategory";
}

/**
 * Renders 3–4 H2 sections with substantive localized body copy at the bottom of high-value
 * landing pages. Boosts word count, heading structure, and text-to-code ratio for SEO.
 * Content lives in src/i18n/locales/*.json under `seoLongform.<ns>`.
 */
export const SeoLongform = ({ ns }: Props) => {
  const { t } = useTranslation();
  const sectionsRaw = t(`seoLongform.${ns}.sections`, { returnObjects: true, defaultValue: [] });
  const sections: Section[] = Array.isArray(sectionsRaw) ? (sectionsRaw as Section[]) : [];
  const title = t(`seoLongform.${ns}.title`, { defaultValue: "" });
  const intro = t(`seoLongform.${ns}.intro`, { defaultValue: "" });

  if (!sections.length && !intro) return null;

  return (
    <section className="py-16 px-4 bg-background border-t border-border">
      <div className="container mx-auto max-w-4xl">
        {title ? (
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">{title}</h2>
        ) : null}
        {intro ? (
          <p className="text-muted-foreground text-lg leading-relaxed mb-10">{intro}</p>
        ) : null}
        <div className="space-y-10">
          {sections.map((s, i) => (
            <div key={i}>
              {s.h ? (
                <h3 className="text-2xl font-semibold text-foreground mb-3">{s.h}</h3>
              ) : null}
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{s.p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SeoLongform;
